const SECRET_KEY = 'hyperscoutxsecurityx2026xvYxsjhmxbsSjaochx'

/** Hex string to Uint8Array (browser-safe replacement for Buffer.from(hex, 'hex')) */
function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return bytes
}

const DECRYPTION_KEY_STORAGE_KEY = 'hyperscout_decryption_key_raw'

/** ArrayBuffer to base64 for localStorage */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

/** Base64 from localStorage to ArrayBuffer */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

/** Uint8Array to hex string */
function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** Cached AES key derived from SECRET_KEY (SHA-256) for use with Web Crypto; restored from localStorage when present */
let cachedKey = null
async function getDecryptionKey() {
  if (cachedKey) return cachedKey

  const stored =
    typeof localStorage !== 'undefined' && localStorage.getItem(DECRYPTION_KEY_STORAGE_KEY)

  if (stored) {
    try {
      const rawBuffer = base64ToArrayBuffer(stored)
      cachedKey = await crypto.subtle.importKey('raw', rawBuffer, { name: 'AES-GCM' }, false, [
        'decrypt'
      ])
      return cachedKey
    } catch {
      // Invalid or corrupted stored key; fall through to derive and re-store
    }
  }

  const keyData = new TextEncoder().encode(SECRET_KEY)
  const hashBuffer = await crypto.subtle.digest('SHA-256', keyData)
  cachedKey = await crypto.subtle.importKey('raw', hashBuffer, { name: 'AES-GCM' }, false, [
    'decrypt'
  ])

  if (typeof localStorage !== 'undefined') {
    const rawExported = await crypto.subtle.exportKey('raw', cachedKey)
    localStorage.setItem(DECRYPTION_KEY_STORAGE_KEY, arrayBufferToBase64(rawExported))
  }

  return cachedKey
}

/** Derive the AES-GCM key with encrypt+decrypt usage (not cached alongside the decrypt-only key) */
async function getEncryptionKey() {
  const keyData = new TextEncoder().encode(SECRET_KEY)
  const hashBuffer = await crypto.subtle.digest('SHA-256', keyData)
  return crypto.subtle.importKey('raw', hashBuffer, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt'
  ])
}

/**
 * Encrypts a plain-text string using AES-256-GCM and returns a colon-separated
 * hex string in the format  iv:authTag:ciphertext  — the exact format expected
 * by decryptContactsData (and the Python decrypt() counterpart).
 */
export const encryptContactsData = async (plainText) => {
  if (!plainText || typeof plainText !== 'string') return ''

  const IV_LENGTH = 12
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))

  const key = await getEncryptionKey()
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    new TextEncoder().encode(plainText)
  )

  // Web Crypto returns ciphertext + 16-byte auth tag concatenated
  const encryptedBytes = new Uint8Array(encryptedBuffer)
  const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - 16)
  const authTag = encryptedBytes.slice(encryptedBytes.length - 16)

  return `${bytesToHex(iv)}:${bytesToHex(authTag)}:${bytesToHex(ciphertext)}`
}

export const decryptContactsData = async (encryptedStr) => {
  if (!encryptedStr || typeof encryptedStr !== 'string') return ''

  const [ivHex, authTagHex, ciphertextHex] = encryptedStr.split(':')

  const iv = hexToBytes(ivHex)
  const authTag = hexToBytes(authTagHex)
  const ciphertext = hexToBytes(ciphertextHex)

  // Web Crypto AES-GCM expects ciphertext + authTag as the encrypted data
  const encrypted = new Uint8Array(ciphertext.length + authTag.length)
  encrypted.set(ciphertext)
  encrypted.set(authTag, ciphertext.length)

  const key = await getDecryptionKey()
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    encrypted
  )
  return new TextDecoder().decode(decrypted)
}
